import { useParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import DetailsTable from 'src/components/DetailsTable';
import SummaryStatCard from 'src/components/summary/SummaryStatCard';
import { usePolicyDetails } from 'src/hooks/environmentWdacPolicy';
import { formatDate } from 'src/utils/formatTime';
import { getBasePolicyModeLabel } from 'src/utils/enums/basePolicyMode';
import { getWdacPolicyPurposeLabel } from 'src/utils/enums/wdacPolicyPurpose';
import { WDAC_POLICY_TYPE, getWdacPolicyTypeLabel } from 'src/utils/enums/wdacPolicyType';
import { BasePolicyLink, RuleOptionsList } from '../../PolicyVersionParts';

const MONOSPACE_SX = { fontFamily: 'monospace', fontSize: '0.75rem' };
const yesNo = value => (value ? 'Yes' : 'No');
const isBase = policy => policy.policyType === WDAC_POLICY_TYPE.BasePolicy;

// What makes a managed policy notable differs by type: a base can be the environment default, while
// a supplemental is there for a purpose.
const managedSubtext = policy => {
  if (!policy.isPortalManaged) {
    return null;
  }

  if (isBase(policy)) {
    return policy.environmentDefault ? 'Environment default' : null;
  }

  return policy.policyPurpose ? getWdacPolicyPurposeLabel(policy.policyPurpose) : null;
};

const toSummaryItems = policy => [
  {
    key: 'currentVersion',
    label: 'Current Version',
    value: policy.latestVersion.policyVersion,
    subtext: policy.policyId,
  },
  {
    key: 'type',
    label: 'Type',
    value: getWdacPolicyTypeLabel(policy.policyType),
    subtext: isBase(policy)
      ? `Mode: ${getBasePolicyModeLabel(policy.latestVersion.basePolicyMode) || '--'}`
      : null,
  },
  {
    key: 'source',
    label: 'Source',
    value: policy.isPortalManaged ? 'Managed' : 'Not Managed',
    subtext: managedSubtext(policy),
  },
  { key: 'createdAt', label: 'Date Added', value: formatDate(policy.createdAt) },
];

// The policy and its latest version read as one thing here; the versions tab has the rest. Mode
// only means something on a base and the base policy only on a supplemental, so each row says
// which kind of policy it appears for.
const DETAIL_ROWS = [
  { key: 'policyName', label: 'Name', render: policy => policy.policyName },
  {
    key: 'policyId',
    label: 'Policy ID',
    render: policy => <Typography sx={MONOSPACE_SX}>{policy.policyId}</Typography>,
  },
  { key: 'policyType', label: 'Type', render: policy => getWdacPolicyTypeLabel(policy.policyType) },
  {
    key: 'basePolicyMode',
    label: 'Mode',
    appliesTo: isBase,
    render: policy => getBasePolicyModeLabel(policy.latestVersion.basePolicyMode),
  },
  {
    key: 'basePolicy',
    label: 'Base Policy',
    appliesTo: policy => !isBase(policy),
    render: policy => <BasePolicyLink version={policy.latestVersion} />,
  },
  {
    key: 'policyPurpose',
    label: 'Purpose',
    render: policy =>
      policy.policyPurpose ? getWdacPolicyPurposeLabel(policy.policyPurpose) : 'N/A',
  },
  {
    key: 'currentVersion',
    label: 'Current Version',
    render: ({ latestVersion }) => (
      <Stack direction="row" alignItems="baseline" gap={0.75} flexWrap="wrap">
        {latestVersion.policyVersion}
        <Box component="span" sx={{ color: '#9CA3AF', fontSize: '0.65rem' }}>
          ({formatDate(latestVersion.createdAt)})
        </Box>
      </Stack>
    ),
  },
  { key: 'isPortalManaged', label: 'Managed', render: policy => yesNo(policy.isPortalManaged) },
  { key: 'isReported', label: 'Detected', render: policy => yesNo(policy.isReported) },
  {
    key: 'isHidden',
    label: 'Visibility',
    render: policy => (policy.isHidden ? 'Hidden' : 'Visible'),
  },
  {
    key: 'ruleOptions',
    label: 'Rule Options',
    render: policy => (
      <RuleOptionsList ruleOptions={policy.latestVersion.ruleOptions} fallback="-" />
    ),
  },
];

const PolicyDetailsGeneral = () => {
  const { policyId } = useParams();
  const { data: policy } = usePolicyDetails({ envPolicyId: policyId });

  if (!policy) {
    return null;
  }

  return (
    <Stack gap={2}>
      <SummaryStatCard items={toSummaryItems(policy)} />
      <DetailsTable
        rows={DETAIL_ROWS.filter(row => !row.appliesTo || row.appliesTo(policy)).map(row => ({
          key: row.key,
          label: row.label,
          value: row.render(policy),
        }))}
      />
    </Stack>
  );
};

export default PolicyDetailsGeneral;
